import { createApp } from 'vue';
import { ElMessage } from 'element-plus';
import { env } from '@/config/env';
import App from './App.vue';
import installElementPlus from './plugins/element';
import store from './store';
import router from './router';
import Viewer from 'v-viewer';
import 'viewerjs/dist/viewer.css';
import { STORAGE_KEYS, getBaseNameByLang } from '@/constants/app';
import { resolveAppLocale } from '@/config/localeSupport';
import '@/assets/themes-base/index.css';
import '@/assets/iconfont/iconfont.css';
import { setupPermissionGuards } from './permission';
import 'default-passive-events';
import './styles/index.scss';
import i18n, { setupI18nMessages } from './i18n';
import ArrowLeft from '@element-plus/icons/es/ArrowLeft';
import Check from '@element-plus/icons/es/Check';
import CircleCloseFilled from '@element-plus/icons/es/CircleCloseFilled';
import Close from '@element-plus/icons/es/Close';
import DocumentCopy from '@element-plus/icons/es/DocumentCopy';
import Minus from '@element-plus/icons/es/Minus';
import Plus from '@element-plus/icons/es/Plus';
import Refresh from '@element-plus/icons/es/Refresh';
import RefreshLeft from '@element-plus/icons/es/RefreshLeft';
import Search from '@element-plus/icons/es/Search';
import Upload from '@element-plus/icons/es/Upload';
import AppPagination from '@/components/AppPagination.vue';
import globalPaginationMixin from '@/mixins/globalPaginationMixin';
import { getCurrentAccessTokenState, removeToken } from '@/utils/auth';
import { isNetworkError } from '@/utils/authSession.mjs';
import { clearOrgUiConfigCache } from '@/utils/orgUiConfigCache';
import { rememberCurrentLoginRedirect } from '@/utils/loginRedirect';
import { setupAccessTokenRefreshLifecycle } from '@/utils/silentAuth';

if (process.env.NODE_ENV === 'development') {
    import('./mock');
}

const ElIconModules = {
    ArrowLeft,
    Check,
    CircleCloseFilled,
    Close,
    DocumentCopy,
    Minus,
    Plus,
    Refresh,
    RefreshLeft,
    Search,
    Upload,
};

function toKebabCase(name) {
    return name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function applyFavicon() {
    const href = `${env.BASE_OSS || ''}${env.BASE_ICO || ''}`.trim();
    if (!href) {
        return;
    }
    const el = document.getElementById('app-favicon') || document.querySelector("link[rel~='icon']");
    if (el) {
        el.setAttribute('href', href);
    }
}
applyFavicon();

function setPageTitle(lang) {
    const title = getBaseNameByLang(lang);
    if (title) {
        document.title = title;
    }
}

function handleLifecycleRefreshFailure(error) {
    const tokenState = getCurrentAccessTokenState();
    if (tokenState !== 'expired' && tokenState !== 'unknown') {
        if (isNetworkError(error)) {
            ElMessage({ type: 'error', message: i18n.global.t('httpNetworkUnavailable') });
        }
        return;
    }

    rememberCurrentLoginRedirect(router.currentRoute?.value);
    removeToken();
    clearOrgUiConfigCache();
    ElMessage({
        type: 'error',
        message: i18n.global.t(isNetworkError(error) ? 'httpNetworkUnavailable' : 'loginSessionExpired')
    });
    if (router.currentRoute?.value?.path !== '/') {
        router.replace({ path: '/' }).catch(() => {});
    }
}

async function bootstrap() {
    const lang = resolveAppLocale();
    await setupI18nMessages(lang);

    const app = createApp(App);

    window.localStorage.setItem(STORAGE_KEYS.lang, lang);
    setPageTitle(lang);

    app
        .use(i18n)
        .use(store)
        .use(Viewer);

    setupPermissionGuards(app);
    app.use(router);

    app.mixin(globalPaginationMixin);

    Viewer.setDefaults({
        Options: {
            inline: true,
            button: true,
            navbar: true,
            title: true,
            toolbar: true,
            tooltip: true,
            movable: true,
            zoomable: true,
            rotatable: true,
            scalable: true,
            transition: true,
            fullscreen: true,
            keyboard: true,
            url: 'data-source'
        }
    });

    await installElementPlus(app, lang);

    for (const iconName in ElIconModules) {
        const comp = ElIconModules[iconName];
        if (comp) {
            app.component(iconName, comp);
            app.component(toKebabCase(iconName), comp);
        }
    }

    app.component('AppPagination', AppPagination);
    app.component('OrderPagination', AppPagination);

    store.dispatch('RestoreHeaderSession');

    app.mount('#app');
    setupAccessTokenRefreshLifecycle({
        onRefreshFailure: handleLifecycleRefreshFailure
    });
}

bootstrap().catch((err) => {
    console.error('App bootstrap failed', err);
});
